import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Tooltip } from 'antd';
import {
    ArrowLeftOutlined,
    AuditOutlined,
    CheckOutlined,
    CopyOutlined,
    FileTextOutlined,
    InfoCircleOutlined,
    Loading3QuartersOutlined,
    ReadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AICommentDetailPanel.css';
import { getCommentExpandedMarkdown, getCommentSummaryMarkdown } from '../../../utils/aiCommentFormatters';
import { getRecommendationText } from '../../../utils/recommendationSections';
import { safeHtml } from '../../../utils/sanitizeHtml';
import { createRenderingT } from '../../../utils/renderingLabels';
import InlineFeedbackTrigger from '../feedback/InlineFeedbackTrigger';
import FastGlobalAnalysisPanel, { buildFastGlobalAnalysis } from './FastGlobalAnalysisPanel';
import {
    formatComparisonChangeSummary,
    formatComparisonMetadataValue,
    getComparisonConfidenceBand,
    getComparisonMetadata,
    hasComparisonAmbiguity,
    isComparisonChangeComment,
    isComparisonRiskComment,
    isRemovedComparisonClause,
} from '../../../utils/comparisonRisk';

const htmlToText = (html = '') => {
    if (!html) return '';
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent?.replace(/\n{3,}/g, '\n\n').trim() || '';
    } catch (e) {
        return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
};

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeMemoSectionLabels = (html = '', t) => {
    if (!html) return '';

    const labelGroups = [
        {
            to: t('ai_comment_detail.memo_labels.strategic_reading', 'Lecture strategique'),
            from: ['Angle dominant'],
        },
        {
            to: t('ai_comment_detail.memo_labels.legal_mechanism', 'Mecanisme contractuel'),
            from: [
                "Pourquoi c'est exploitable",
                'Mecanisme exploitable',
                'Mecanisme juridique',
                'Mécanisme juridique',
                'Mecanisme contractuel',
                'Mécanisme contractuel',
                'Fonctionnement de la clause',
                'Comment cette clause fonctionne',
            ],
        },
        {
            to: t('ai_comment_detail.memo_labels.operational_impact', 'Impact operationnel'),
            from: ['Consequence operationnelle', 'Impact concret'],
        },
        {
            to: t('ai_comment_detail.memo_labels.litigation_exposure', 'Risque de litige'),
            from: [
                'Projection contentieuse',
                'Exposition en litige',
                'Exposition contentieuse',
                'Risque de litige',
                'Risque en cas de désaccord',
                'Risque en cas de desaccord',
                'Si un désaccord survient',
                'Si un desaccord survient',
            ],
        },
        {
            to: t('ai_comment_detail.memo_labels.recommended_actions', 'Leviers recommandes'),
            from: ['Ce que vous devez faire'],
        },
        {
            to: t('ai_comment_detail.memo_labels.recommended_decision', 'Orientation de revue'),
            from: ['Signal decisionnel'],
        },
        {
            to: t('ai_comment_card.recommendation', 'Piste de traitement'),
            from: ['Recommandation'],
        },
        {
            to: t('ai_comment_card.primary_action', 'Levier prioritaire à examiner'),
            from: ['Action prioritaire', 'Action recommandée', 'Action recommandee'],
        },
        {
            to: t('ai_comment_card.drafting_target', 'Exemple de formulation à valider'),
            from: ['Rédaction cible', 'Redaction cible'],
        },
        {
            to: t('ai_comment_card.alternative_version', 'Option alternative à examiner'),
            from: ['Alternative'],
        },
    ];

    return labelGroups.reduce((content, group) => {
        const pattern = group.from.map(escapeRegExp).join('|');
        const strongLabel = new RegExp(`(<strong>\\s*)(?:${pattern})(\\s*:?)\\s*(<\\/strong>)`, 'giu');
        const plainLabel = new RegExp(`\\b(?:${pattern})(\\s*:)`, 'giu');
        return content
            .replace(strongLabel, `$1${group.to}$2$3`)
            .replace(plainLabel, `${group.to}$1`);
    }, String(html));
};

const stripMemoSourceLine = (html = '') => {
    if (!html) return '';

    return String(html)
        .replace(
            /<p>\s*(?:<strong>\s*)?Source\s+de\s+l['’â€™]analyse\s*:?(?:\s*<\/strong>)?[\s\S]*?<\/p>/giu,
            ''
        )
        .replace(
            /(?:^|\n)\s*Source\s+de\s+l['’â€™]analyse\s*:?.*$/gimu,
            ''
        )
        .trim();
};

const stripMemoLeadingTitle = (html = '') => {
    if (!html) return '';

    return String(html)
        .replace(
            /^\s*<(?:h1|h2|h3|p)>\s*(?:<strong>\s*)?(?:m(?:e|é|Ã©)mo(?:\s+de\s+revue|\s+strat(?:e|é|Ã©)gique)?|memo(?:\s+de\s+revue|\s+strat(?:e|é|Ã©)gique)?)(?:\s*<\/strong>)?\s*<\/(?:h1|h2|h3|p)>\s*/iu,
            ''
        )
        .replace(
            /^\s*(?:m(?:e|é|Ã©)mo(?:\s+de\s+revue|\s+strat(?:e|é|Ã©)gique)?|memo(?:\s+de\s+revue|\s+strat(?:e|é|Ã©)gique)?)\s*[\r\n]+/iu,
            ''
        )
        .trim();
};

const normalizeRiskLevel = (risk) => {
    if (!risk) return null;

    const rawRisk = risk && typeof risk === 'object'
        ? risk.level || risk.risk || risk.severity
        : risk;
    const riskLower = String(rawRisk).toLowerCase().trim();

    const frenchMapping = {
        faible: 'low',
        moyen: 'medium',
        eleve: 'high',
        élevé: 'high',
        'ã©levã©': 'high',
        critique: 'critical',
    };

    const englishMapping = {
        low: 'low',
        medium: 'medium',
        high: 'high',
        critical: 'critical',
    };

    return frenchMapping[riskLower] || englishMapping[riskLower] || null;
};

const compactExcerpt = (value, maxLength = 520) => {
    const text = String(value || '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!text || text.length <= maxLength) {
        return { text, truncated: false };
    }
    return {
        text: `${text.slice(0, maxLength - 1).trim()}...`,
        truncated: true,
    };
};

const AICommentDetailPanel = ({
    visible,
    onClose,
    comment,
    labelsVariant = null,
    fastGlobalDetail = null,
    onFastGlobalRetry = null,
}) => {
    const { t: tReview } = useTranslation('review');
    const tRendering = useMemo(
        () => createRenderingT(tReview, labelsVariant),
        [tReview, labelsVariant]
    );
    const [streamedContent, setStreamedContent] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [expandedExtracts, setExpandedExtracts] = useState({});
    const [copied, setCopied] = useState(false);
    const contentScrollRef = useRef(null);
    const commentId = comment?.id;
    const commentMoreContent = comment?.more_content;
    const commentMoreStatus = comment?.more_status;
    const commentErrorTitle = tReview('ai_comment_detail.error.title');
    const scrollMemoToTop = useCallback(() => {
        const el = contentScrollRef.current;
        if (!el) return;
        el.scrollTop = 0;
        requestAnimationFrame(() => {
            if (contentScrollRef.current) {
                contentScrollRef.current.scrollTop = 0;
            }
        });
    }, []);

    useEffect(() => {
        if (!visible || !commentId) return;
        setStreamedContent(commentMoreContent || '');
        setStreaming(commentMoreStatus === 'running');
        setError(commentMoreStatus === 'error' ? commentErrorTitle : null);
        setExpanded(false);
        setExpandedExtracts((prev) => (Object.keys(prev).length > 0 ? {} : prev));
        scrollMemoToTop();
    }, [visible, commentId, commentMoreContent, commentMoreStatus, commentErrorTitle, scrollMemoToTop]);

    const summaryMarkdown = comment ? getCommentSummaryMarkdown(comment, tRendering) : '';
    const expandedMarkdown = comment ? getCommentExpandedMarkdown(comment, tRendering) : '';
    const hasContextContent = Boolean(summaryMarkdown || expandedMarkdown);
    const displayMemoContent = useMemo(() => {
        const normalized = normalizeMemoSectionLabels(streamedContent, tRendering);
        return stripMemoLeadingTitle(stripMemoSourceLine(normalized));
    }, [streamedContent, tRendering]);
    const plainMemo = useMemo(() => htmlToText(displayMemoContent), [displayMemoContent]);

    useLayoutEffect(() => {
        if (!visible || !commentId || !displayMemoContent || streaming) return;
        scrollMemoToTop();
    }, [visible, commentId, displayMemoContent, streaming, scrollMemoToTop]);

    const sourceLabel = [
        comment?.criterion_title,
        comment?.page ? `${tReview('page_short', 'p.')} ${comment.page}` : null,
    ].filter(Boolean).join(' / ');

    const normalizedRiskLevel = useMemo(
        () => normalizeRiskLevel(comment?.risk),
        [comment?.risk]
    );

    const riskLabel = normalizedRiskLevel
        ? tReview(`risk.${normalizedRiskLevel}`)
        : null;
    const comparisonMetadata = useMemo(
        () => getComparisonMetadata(comment),
        [comment]
    );
    const isFastGlobalCard = comparisonMetadata?.review_mode === 'fast_global';
    const fastGlobalAnalysis = useMemo(() => buildFastGlobalAnalysis({
        metadataJson: comment?.metadata_json,
        quickView: comment?.quick_view,
        impact: comment?.impact || comment?.detailed_view?.impact,
        quote: comment?.quote,
        ref: comment?.ref || comment?.doc_ref,
        page: comment?.page,
        targetBlockIds: comment?.target_block_ids,
    }), [comment]);
    const effectiveFastGlobalDetail = isFastGlobalCard
        ? (fastGlobalDetail || { status: 'running', sections: {} })
        : null;
    const activeStreaming = isFastGlobalCard
        ? effectiveFastGlobalDetail.status === 'running'
        : streaming;
    const isComparisonRisk = isComparisonRiskComment(comment);
    const isComparisonChange = isComparisonChangeComment(comment);
    const isRemovedClause = isRemovedComparisonClause(comment);
    const hasAmbiguityWarning = hasComparisonAmbiguity(comment);
    const comparisonAction = getRecommendationText(comment?.detailed_view?.recommendation)
        || comment?.quick_view?.action
        || comment?.suggested_action
        || '';
    const comparisonRiskText = comment?.quick_view?.risk
        || comment?.quick_view?.description
        || comment?.description
        || '';
    const shouldRenderMemoSurface = !isFastGlobalCard && (!isComparisonChange || streaming || error || Boolean(displayMemoContent));
    const comparisonChangeSummary = formatComparisonChangeSummary(
        comparisonMetadata?.change_summary,
        comparisonMetadata?.change_type,
        tReview
    );
    const comparisonConfidenceBand = getComparisonConfidenceBand(comparisonMetadata);
    const isUncertainComparisonChange = isComparisonChange && ['low', 'unknown'].includes(comparisonConfidenceBand);
    const shouldCompactComparisonExtracts = isComparisonChange;
    const detailEyebrow = isFastGlobalCard
        ? tReview('ai_comment_card.analysis.open', 'Approfondir')
        : isComparisonChange
        ? tReview('comparison.badge_risk', 'Comparaison')
        : tReview('ai_comment_card.ai_analysis', 'Analyse IA');
    const detailTitle = isFastGlobalCard
        ? tReview('ai_comment_card.analysis.panelLabel', 'Analyse structurée du risque')
        : isUncertainComparisonChange
        ? tReview('comparison.detail_title_uncertain', 'Correspondance incertaine')
        : isComparisonChange
            ? tReview('comparison.detail_title_change', 'Détail du changement')
            : tReview('ai_comment_detail.memo_title', 'Memo strategique');
    const shouldRenderContextSummary = hasContextContent && !isComparisonChange && !isFastGlobalCard;
    const renderComparisonExtract = (key, label, value, fallback) => {
        const rawText = String(value || fallback || '').trim();
        const compact = compactExcerpt(rawText);
        const isExpanded = Boolean(expandedExtracts[key]);
        const displayedText = shouldCompactComparisonExtracts && compact.truncated && !isExpanded
            ? compact.text
            : rawText;

        return (
            <div className="comparison-risk-detail__extract">
                <strong>{label}</strong>
                <p>{displayedText}</p>
                {shouldCompactComparisonExtracts && compact.truncated && (
                    <button
                        type="button"
                        className="comparison-risk-detail__expand"
                        onClick={() => setExpandedExtracts((prev) => ({
                            ...prev,
                            [key]: !prev[key],
                        }))}
                    >
                        {isExpanded
                            ? tReview('comparison.hide_full_excerpt', "Masquer l'extrait complet")
                            : tReview('comparison.show_full_excerpt', "Afficher l'extrait complet")}
                    </button>
                )}
            </div>
        );
    };

    const copyMemo = async () => {
        if (!plainMemo) return;
        try {
            await navigator.clipboard.writeText(plainMemo);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            setCopied(false);
        }
    };

    if (!visible || !comment) return null;

    return (
        <section className="ai-comment-detail-panel has-inline-feedback is-active" aria-live={activeStreaming ? 'polite' : 'off'}>
            <header className="ai-memo-header">
                <div className="ai-memo-header__top">
                    <Tooltip title={tReview('ai_comment_detail.back_to_comments', 'Retour aux commentaires')}>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={onClose}
                            className="ai-memo-back"
                            aria-label={tReview('ai_comment_detail.back_to_comments', 'Retour aux commentaires')}
                        />
                    </Tooltip>

                    {activeStreaming ? (
                        <div className="ai-memo-live-pill">
                            <Loading3QuartersOutlined spin />
                            <span>{tReview('ai_comment_detail.streaming', 'Analyse en cours')}</span>
                        </div>
                    ) : (
                        <div className="ai-memo-live-pill is-idle" aria-hidden="true" />
                    )}

                    <InlineFeedbackTrigger
                        featureArea="comment_details"
                        targetType="memo"
                        targetId={comment?.id || null}
                        aiCommentId={comment?.id || null}
                        contextJson={{
                            source: "ai_comment_detail_panel",
                            comment_title: comment?.title || null,
                            criterion_title: comment?.criterion_title || null,
                            risk_level: normalizedRiskLevel || null,
                            memo_status: activeStreaming ? "streaming" : (error ? "error" : "ready"),
                        }}
                        badgeLabel={tReview("feedback.badges.comment_details")}
                        tooltip={tReview("feedback.memoTooltip")}
                        className="ai-memo-feedback-trigger"
                    />
                </div>

                <div className="ai-memo-title-block">
                    <div className="ai-memo-eyebrow">
                        <AuditOutlined />
                        <span>{detailEyebrow}</span>
                    </div>
                    <h2>{detailTitle}</h2>
                </div>

                <div className="ai-memo-meta-row">
                    {sourceLabel && <span>{sourceLabel}</span>}
                    {riskLabel && <span>{riskLabel}</span>}
                </div>
            </header>

            <div className="ai-memo-scroll" ref={contentScrollRef}>
                <div className="ai-memo-context">
                    <div className="ai-memo-context__label">
                        <FileTextOutlined />
                        <span>{tReview('ai_comment_detail.context', 'Contexte')}</span>
                    </div>

                    <div className="ai-memo-context__title">{comment.title}</div>

                    {shouldRenderContextSummary && (
                        <div className={`ai-memo-context__summary ${expanded ? 'expanded' : ''}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {expanded ? expandedMarkdown : summaryMarkdown}
                            </ReactMarkdown>

                            {expandedMarkdown && expandedMarkdown !== summaryMarkdown && (
                                <button
                                    type="button"
                                    className={`ai-memo-expand ${expanded ? 'expanded' : ''}`}
                                    onClick={() => setExpanded(!expanded)}
                                >
                                    {expanded
                                        ? tReview('ai_comment_detail.see_less')
                                        : tReview('ai_comment_detail.see_more')}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {isFastGlobalCard && (
                    <FastGlobalAnalysisPanel
                        id={`fast-global-detail-${comment.id}`}
                        analysis={fastGlobalAnalysis}
                        detail={effectiveFastGlobalDetail}
                        onRetry={onFastGlobalRetry}
                    />
                )}

                {isComparisonRisk && comparisonMetadata && (
                    <section
                        className="comparison-risk-detail"
                        aria-label={tReview('comparison.context_aria', 'Contexte de comparaison')}
                    >
                        <div className="comparison-risk-detail__header">
                            <span className="comparison-risk-detail__eyebrow">
                                {isComparisonChange && ['low', 'unknown'].includes(comparisonConfidenceBand)
                                    ? tReview('comparison.badge_uncertain', 'Correspondance incertaine')
                                    : isComparisonChange
                                    ? tReview('comparison.badge_change', 'Changement détecté')
                                    : tReview('comparison.badge_risk', 'Comparaison')}
                            </span>
                            {isRemovedClause && (
                                <span className="comparison-risk-detail__status">
                                    {tReview(
                                        'comparison.removed_status',
                                        'Présente dans la référence, absente du contrat reçu'
                                    )}
                                </span>
                            )}
                        </div>

                        <div className="comparison-risk-detail__chips">
                            {hasAmbiguityWarning && (
                                <Tooltip title={tReview(
                                    'comparison.uncertain_match_description',
                                    'Le commentaire repose sur une correspondance probable entre les deux versions.'
                                )}>
                                    <span className="comparison-risk-detail__coverage comparison-risk-detail__coverage--uncertain">
                                        <InfoCircleOutlined />
                                        {tReview('comparison.uncertain_match_title', 'Correspondance incertaine')}
                                    </span>
                                </Tooltip>
                            )}
                            {formatComparisonMetadataValue(comparisonMetadata.change_type, tReview) && (
                                <span>{formatComparisonMetadataValue(comparisonMetadata.change_type, tReview)}</span>
                            )}
                            {formatComparisonMetadataValue(comparisonMetadata.match_method, tReview) && (
                                <span>
                                    {tReview('comparison.method_label', 'Méthode')} : {formatComparisonMetadataValue(comparisonMetadata.match_method, tReview)}
                                </span>
                            )}
                        </div>

                        {comparisonChangeSummary && (
                            <div className="comparison-risk-detail__summary">
                                <strong>{tReview('comparison.change_summary_label', 'Résumé du changement')}</strong>
                                <p>{comparisonChangeSummary}</p>
                            </div>
                        )}

                        <div className="comparison-risk-detail__extracts">
                            {renderComparisonExtract(
                                'base',
                                tReview('comparison.reference_excerpt', 'Extrait référence'),
                                comparisonMetadata.base_text,
                                tReview('comparison.unavailable', 'Non disponible.')
                            )}
                            {renderComparisonExtract(
                                'received',
                                tReview('comparison.received_excerpt', 'Extrait contrat reçu'),
                                comparisonMetadata.new_text,
                                isRemovedClause
                                    ? tReview('comparison.absent_received', 'Absent du contrat reçu.')
                                    : tReview('comparison.unavailable', 'Non disponible.')
                            )}
                        </div>

                        {comparisonMetadata.ambiguity_reason && (
                            <div className="comparison-risk-detail__summary">
                                <strong>{tReview('comparison.ambiguity_reason', "Raison d'ambiguïté")}</strong>
                                <p>{comparisonMetadata.ambiguity_reason}</p>
                            </div>
                        )}

                        <div className="comparison-risk-detail__risk-grid">
                            {!isComparisonChange && comparisonRiskText && (
                                <div>
                                    <strong>{tReview('ai_comment_card.risk_level_label', 'Risque')}</strong>
                                    <p>{comparisonRiskText}</p>
                                </div>
                            )}
                            {comment?.detailed_view?.impact && (
                                <div>
                                    <strong>{tReview('ai_comment_card.impact', 'Impact')}</strong>
                                    <p>{comment.detailed_view.impact}</p>
                                </div>
                            )}
                            {comment?.detailed_view?.justification && (
                                <div>
                                    <strong>{tReview('ai_comment_card.justification', 'Justification')}</strong>
                                    <p>{comment.detailed_view.justification}</p>
                                </div>
                            )}
                            {!isComparisonChange && comparisonAction && (
                                <div>
                                    <strong>{tReview('ai_comment_card.action_label', 'Levier recommandé')}</strong>
                                    <p>{comparisonAction}</p>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {shouldRenderMemoSurface && (
                <div className={`ai-memo-surface ${streaming ? 'is-streaming' : ''}`}>
                    <div className="ai-memo-surface__bar">
                        <div className="ai-memo-surface__heading">
                            <span className="ai-memo-surface__icon" aria-hidden="true">
                                <ReadOutlined />
                            </span>
                            <span>{tReview('ai_comment_detail.memo', 'Analyse')}</span>
                        </div>

                        <Tooltip
                            title={copied
                                ? tReview('sidebar.resumePanelCopied', { defaultValue: 'Copie' })
                                : tReview('sidebar.resumePanelCopy', { defaultValue: 'Copier' })}
                        >
                            <Button
                                type="text"
                                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                                onClick={copyMemo}
                                disabled={!plainMemo}
                                className={`ai-memo-copy ${copied ? 'is-copied' : ''}`}
                                aria-label={tReview('sidebar.resumePanelCopy', { defaultValue: 'Copier' })}
                            />
                        </Tooltip>
                    </div>

                    {error && (
                        <Alert
                            message={tReview('ai_comment_detail.error.title')}
                            description={error}
                            type="error"
                            showIcon
                            className="ai-memo-error"
                        />
                    )}

                    {displayMemoContent ? (
                        <article
                            className="ai-memo-prose"
                            dangerouslySetInnerHTML={safeHtml(displayMemoContent)}
                        />
                    ) : (
                        <div className="ai-memo-empty">
                            {streaming ? (
                                <div className="ai-memo-skeleton">
                                    <span />
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            ) : (
                                <>
                                    <FileTextOutlined />
                                    <p>{tReview('ai_comment_detail.empty')}</p>
                                </>
                            )}
                        </div>
                    )}
                </div>
                )}
            </div>
        </section>
    );
};

export default AICommentDetailPanel;
